import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useUnusedLines } from '@/hooks/useLineAnalytics';
import { useExportUnusedLines } from '@/hooks/useLineExports';
import { fadeUp, stagger } from '@/lib/animations';
import { statusMapping } from '@/lib/statusMapping';
import { cn } from '@/lib/utils';
import { UnusedLinesFilters } from '@/types/mobile-line.dto';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AnimatePresence, motion } from 'framer-motion';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import {
  CalendarIcon,
  Download,
  Info,
  Loader2,
  Phone,
  Search,
  User,
} from 'lucide-react';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';

function formatPhoneNumber(number: string): string {
  if (!number) return '';
  try {
    const phone = parsePhoneNumberWithError(number, 'FR');
    return phone.formatNational();
  } catch {
    return number;
  }
}

function getStatusDotColor(status: string): string {
  if (status === 'used') return 'bg-emerald-500';
  if (status === 'restricted' || status === 'restrictionInProgress')
    return 'bg-amber-500';
  if (
    status === 'suspended' ||
    status === 'suspensionInProgress'
  )
    return 'bg-orange-500';
  if (
    status === 'terminated' ||
    status === 'terminationInProgress' ||
    status === 'cancelled'
  )
    return 'bg-red-500';
  return 'bg-slate-400';
}

const DATE_SHORTCUTS = [
  {
    label: 'Ce mois',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth(), 1),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    },
  },
  {
    label: 'Le mois dernier',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        to: new Date(today.getFullYear(), today.getMonth(), 0),
      };
    },
  },
  {
    label: 'Les 3 derniers mois',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth() - 2, 1),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    },
  },
  {
    label: 'Les 6 derniers mois',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth() - 5, 1),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    },
  },
  {
    label: 'Cette année',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), 0, 1),
        to: new Date(today.getFullYear(), 11, 31),
      };
    },
  },
];

function getMatchingShortcut(dateRange: DateRange | undefined): string | null {
  if (!dateRange?.from || !dateRange?.to) return null;
  const fromTime = dateRange.from.getTime();
  const toTime = dateRange.to.getTime();
  for (const shortcut of DATE_SHORTCUTS) {
    const range = shortcut.getRange();
    if (
      range.from &&
      range.to &&
      fromTime === range.from.getTime() &&
      toTime === range.to.getTime()
    ) {
      return shortcut.label;
    }
  }
  return null;
}

interface UnusedLinesTabProps {
  organizationId: string | undefined;
  siteId: string | undefined;
}

export function UnusedLinesTab({ organizationId, siteId }: UnusedLinesTabProps) {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] =
    useState<UnusedLinesFilters | null>(null);

  const {
    data: unusedLines = [],
    isLoading,
    error,
  } = useUnusedLines(organizationId, siteId, appliedFilters);

  const { mutate: exportUnusedLines, isPending: isExporting } =
    useExportUnusedLines(organizationId, siteId);

  const matchingShortcut = getMatchingShortcut(dateRange);

  const handleSearch = () => {
    if (!dateRange?.from || !dateRange?.to) return;
    setAppliedFilters({
      startMonth: dateRange.from.getMonth() + 1,
      startYear: dateRange.from.getFullYear(),
      endMonth: dateRange.to.getMonth() + 1,
      endYear: dateRange.to.getFullYear(),
    });
  };

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <motion.div variants={fadeUp}>
        <div className="rounded-2xl bg-white p-5 shadow-sm space-y-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Lignes inutilisées
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Identifiez les lignes sans consommation sur une période
            </p>
          </div>

          {/* Period chips + calendar */}
          <div className="flex flex-wrap items-center gap-2">
            {DATE_SHORTCUTS.map((shortcut) => {
              const isSelected = matchingShortcut === shortcut.label;
              return (
                <button
                  key={shortcut.label}
                  onClick={() => setDateRange(shortcut.getRange())}
                  className={cn(
                    'rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors',
                    isSelected
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-slate-50 text-slate-600 border-transparent hover:bg-slate-100'
                  )}
                >
                  {shortcut.label}
                </button>
              );
            })}

            {/* Custom date picker */}
            <Popover
              open={dateRangePickerOpen}
              onOpenChange={setDateRangePickerOpen}
            >
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    'rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors flex items-center gap-1.5',
                    dateRange?.from && !matchingShortcut
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-slate-50 text-slate-600 border-transparent hover:bg-slate-100'
                  )}
                >
                  <CalendarIcon className="h-3 w-3" />
                  {dateRange?.from && !matchingShortcut ? (
                    <>
                      {format(dateRange.from, 'dd MMM', { locale: fr })}
                      {dateRange.to && (
                        <> – {format(dateRange.to, 'dd MMM', { locale: fr })}</>
                      )}
                    </>
                  ) : (
                    'Personnalisé'
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 max-w-none"
                align="start"
              >
                <Calendar
                  mode="range"
                  defaultMonth={dateRange?.from || new Date()}
                  selected={dateRange}
                  onSelect={(range) => {
                    if (range) setDateRange(range);
                    if (range?.from && range?.to) setDateRangePickerOpen(false);
                  }}
                  numberOfMonths={2}
                  locale={fr}
                  disabled={(date) => date > new Date()}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Search button */}
          <Button
            onClick={handleSearch}
            disabled={!dateRange?.from || !dateRange?.to || isLoading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5"
            size="sm"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Recherche...
              </>
            ) : (
              <>
                <Search className="mr-1.5 h-3.5 w-3.5" />
                Rechercher
              </>
            )}
          </Button>
        </div>
      </motion.div>

      <motion.div variants={fadeUp}>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Attention, ici on tient compte seulement des consommations sortantes
            (appel émis). Une ligne d'alarme par exemple qui ne contient que des
            consommations entrantes (appel reçu) sera répertoriée comme
            inutilisée car elle n'émet pas d'appel alors qu'elle est peut-être
            utilisée.
          </AlertDescription>
        </Alert>
      </motion.div>

      <AnimatePresence>
        {appliedFilters && (
          <motion.div
            key="unused-lines-results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header with count + export */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  Lignes inutilisées
                  {!isLoading && !error && (
                    <span className="ml-2 text-base font-normal text-slate-400">
                      ({unusedLines.length})
                    </span>
                  )}
                </h3>
                <p className="text-sm text-slate-400">
                  Lignes sans aucune consommation sur la période sélectionnée
                </p>
              </div>
              {!isLoading && !error && unusedLines.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isExporting}
                    >
                      {isExporting ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Exporter
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={() =>
                        exportUnusedLines({
                          filters: appliedFilters,
                          format: 'xlsx',
                        })
                      }
                    >
                      Excel (.xlsx)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        exportUnusedLines({
                          filters: appliedFilters,
                          format: 'csv',
                        })
                      }
                    >
                      CSV (.csv)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="relative rounded-2xl bg-white/60 backdrop-blur-[2px] py-16 flex items-center justify-center">
                <div className="flex items-center gap-3">
                  <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
                  <span className="text-sm text-slate-500">
                    Recherche en cours...
                  </span>
                </div>
              </div>
            ) : error ? (
              <div className="rounded-2xl bg-white py-16 flex flex-col items-center justify-center text-slate-400">
                <p className="text-sm font-medium text-destructive">
                  Erreur lors du chargement des données. Veuillez réessayer.
                </p>
              </div>
            ) : unusedLines.length === 0 ? (
              <div className="rounded-2xl bg-white py-16 flex flex-col items-center justify-center text-slate-400">
                <Phone className="h-10 w-10 mb-3 text-slate-300" />
                <p className="text-sm font-medium">
                  Aucune ligne non utilisée sur cette période
                </p>
                <p className="text-xs mt-1">
                  Toutes les lignes ont eu de la consommation
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {unusedLines.map((line) => {
                  const name =
                    line.firstName || line.lastName
                      ? `${line.firstName || ''} ${line.lastName || ''}`.trim()
                      : null;

                  return (
                    <div
                      key={line.id}
                      className="group flex items-center gap-4 bg-white rounded-xl px-5 py-4 transition-all duration-200 hover:shadow-md border border-transparent hover:border-emerald-100"
                    >
                      {/* Avatar */}
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 group-hover:bg-emerald-50 transition-colors">
                        <User className="h-4 w-4 text-slate-600 group-hover:text-emerald-700 transition-colors" />
                      </div>

                      {/* Content grid */}
                      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-x-6 items-center">
                        {/* Number + Name */}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 tabular-nums tracking-wide">
                            {formatPhoneNumber(line.number)}
                          </p>
                          {name && (
                            <p className="text-xs text-slate-500 truncate mt-0.5">
                              {name}
                            </p>
                          )}
                        </div>

                        {/* Plan */}
                        <div className="min-w-0 hidden sm:block">
                          <p className="text-sm text-slate-700 truncate">
                            {line.phonePlanName || '-'}
                          </p>
                        </div>

                        {/* Status */}
                        <div className="hidden sm:flex items-center gap-2">
                          <span
                            className={`shrink-0 h-2.5 w-2.5 rounded-full ${getStatusDotColor(line.status)}`}
                          />
                          <span className="text-xs text-slate-500">
                            {statusMapping[line.status] || line.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
